/**
 * Cloud agents as team members (roadmap D184, docs/plans/2026-10-cloud-agents.md).
 *
 * Two contracts the AIOE mirrors byte for byte, beside the workbench's own use:
 *
 *  - {@link cloudPlacementSchema}: `runsOn.cloud`, "In the cloud" as a fourth place a member runs
 *    (after this computer, a node and another workbench). The member keeps its role, instructions
 *    and model; its tasks go out to a provider's cloud agent and come back as a branch the team
 *    reviews. A published template may name it: a provider is not a machine.
 *  - {@link cloudAgentsPolicySchema}: the `cloudAgents` clause of `.mojoup/policy.json`. A managed
 *    policy without it allows no cloud at all; cloud work waits for a person's review unless the
 *    clause says otherwise.
 *
 * The provider ids name every provider the plan studied, so the strict schema does not have to be
 * re-mirrored as each connector lands; the workbench says which ones it can drive
 * (`src/cloud/cloudGate.ts`) and refuses the rest in words.
 *
 * Plain zod, no Node: the webview imports the vocabulary in `shared/protocol/cloudAgents.ts`.
 */
import { z } from "zod";
/** Every cloud provider a member may be placed with. */
export const CLOUD_PROVIDER_IDS = ["github-copilot", "cursor", "claude-code", "codex", "jules", "devin", "ci"];
export const cloudProviderIdSchema = z.enum(CLOUD_PROVIDER_IDS);
/**
 * `runsOn.cloud`: which provider's cloud a member works in.
 *
 *  - `provider`: the provider.
 *  - `customAgent`: Copilot's custom agent (`.github/agents/<name>.agent.md`), when the team committed one.
 *  - `environment`: the provider's environment id — Codex cloud's environment (`codex cloud exec --env`),
 *    Cursor's named environment, a Claude self-hosted environment.
 *  - `createPullRequest`: ask the provider for a pull request (default true). The work comes back either way.
 */
export const cloudPlacementSchema = z
    .object({
    provider: cloudProviderIdSchema,
    customAgent: z.string().min(1).max(100).optional(),
    environment: z.string().min(1).max(200).optional(),
    createPullRequest: z.boolean().optional(),
})
    .strict();
/**
 * The `cloudAgents` clause.
 *
 *  - `allowed`: the providers that may receive tasks. Left out, a managed policy (an organisation's)
 *    allows none; a repository's own file allows any.
 *  - `organisationKeys`: whether keys the organisation pays for (a Cursor service account, a Devin
 *    organisation key) may be used, or only each person's own account. Default false.
 *  - `requireHumanReview`: cloud work waits for a person's review before it lands. Default true.
 *  - `maxConcurrent`: cloud tasks running at once in a project, at most.
 */
export const cloudAgentsPolicySchema = z.object({
    allowed: z.array(cloudProviderIdSchema).max(20).optional(),
    organisationKeys: z.boolean().optional(),
    requireHumanReview: z.boolean().optional(),
    maxConcurrent: z.number().int().min(1).max(100).optional(),
});
/**
 * What the organisation records about one cloud run (the AIOE's `POST /cloud-agents/v1/runs` body),
 * and the events the workbench reports on it. The AIOE answers with the run's id and, when it serves
 * the workbench's tools through its relay, the MCP endpoint and the per-run credential the provider
 * is handed. See docs/plans/2026-10-cloud-agents.md, "What the AIOE must build".
 */
export const cloudRunCreateSchema = z.object({
    projectId: z.string().min(1).max(200).optional(),
    rootId: z.string().min(1).max(200).optional(),
    /** The workspace the relay routes the run's tool calls to (the desktop gateway's `workspace` param). */
    workspace: z.string().min(1).max(200).optional(),
    provider: cloudProviderIdSchema,
    team: z.string().min(1).max(200),
    member: z.string().min(1).max(200),
    task: z.string().min(1).max(200),
    /** The tools the run may call through the relay, by name. */
    tools: z.array(z.string().min(1).max(100)).max(64),
    /** When the credential stops working, ISO. */
    expiresAt: z.string().min(1).max(40),
    /** The person the run is started as (their principal with the platform). */
    startedBy: z.string().min(1).max(200).optional(),
});
export const cloudRunCreatedSchema = z.object({
    runId: z.string().min(1).max(200),
    /** Present when the organisation serves the workbench's tools to the provider through its relay. */
    mcp: z.object({ url: z.string().url().max(2000), token: z.string().min(16).max(4000), expiresAt: z.string().max(40).optional() }).optional(),
});
/** One thing that happened to a run, for the organisation's record of it. */
export const cloudRunEventSchema = z.object({
    at: z.string().min(1).max(40),
    kind: z.enum(["started", "progress", "waiting", "handed-back", "steered", "cancelled", "failed", "integrated", "closed"]),
    /** The provider's own task or session id, its page, the pull request. */
    providerTaskId: z.string().max(300).optional(),
    url: z.string().max(2000).optional(),
    pullRequest: z.string().max(2000).optional(),
    /** A sentence, never task text. */
    note: z.string().max(500).optional(),
});
//# sourceMappingURL=cloudAgents.js.map